import { Info } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";
import { XStack, YStack } from "tamagui";
import { useApi } from "../../../core/apis";
import { useStores } from "../../../core/stores";
import { Button } from "../../../ui/Button";
import { CenterContent } from "../../../ui/CenterContent";
import { Pill, PillType } from "../../../ui/Pill";
import { Subtext } from "../../../ui/Subtext";
import { FeedFilter } from "../../plan/BusinessPlanModel";
import { FeedForm } from "./FeedForm";
import { FeedItemUI } from "./FeedItemUI";

export const FeedUI = observer(
  ({ businessPlanUid }: { businessPlanUid: string }) => {
    const { businessPlansStore } = useStores();
    const { businessFeedItemsApi } = useApi();
    const businessPlan = businessPlansStore.byId.get(businessPlanUid);
    const pinnedFeedItems = businessPlan?.feedPinnedItems ?? [];
    const feedItems = businessPlan?.feedItems ?? [];
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = React.useState(false);

    if (!businessPlan) {
      return null;
    }

    return (
      <YStack gap="$medium" width={"100%"}>
        <FeedForm businessPlan={businessPlan} />
        <XStack
          gap={"$medium"}
          alignItems={"center"}
          paddingHorizontal={"$larger"}
        >
          <Subtext>{t("business:feed_filter_by")}</Subtext>
          <Pill
            type={PillType.Muted}
            cursor={"pointer"}
            onPress={() => businessPlan.setFeedFilter(FeedFilter.Media)}
            backgroundColor={
              businessPlan.feedFilter === FeedFilter.Media
                ? "$backgroundBlock"
                : "rgba(0,0,0,.05)"
            }
          >
            <Subtext color="$color">{t("business:feed_media")}</Subtext>
          </Pill>
          <Pill
            type={PillType.Muted}
            cursor={"pointer"}
            onPress={() => businessPlan.setFeedFilter(FeedFilter.Mentions)}
            backgroundColor={
              businessPlan.feedFilter === FeedFilter.Mentions
                ? "$backgroundBlock"
                : "rgba(0,0,0,.05)"
            }
          >
            <Subtext color="$color">{t("business:feed_mentions")}</Subtext>
          </Pill>
        </XStack>

        {pinnedFeedItems?.map((item) => (
          <FeedItemUI key={item.uid} item={item} />
        ))}

        {feedItems.length === 0 && (
          <CenterContent
            icon={Info}
            muted
            title={t("business:feed_empty_title")}
            subTitle={t("business:feed_empty_desc")}
          />
        )}

        {feedItems?.map((item) => (
          <FeedItemUI key={item.uid} item={item} />
        ))}

        {businessPlan.hasMoreFeedItems && (
          <Button
            isLoading={isLoading}
            onPress={async () => {
              setIsLoading(true);

              if (businessPlan.feedFilter === FeedFilter.Mentions) {
                await businessFeedItemsApi.getMentionsInBusinessPlan(
                  businessPlan.uid,
                  businessPlan.userInPlan?.uid,
                  feedItems[feedItems.length - 1].data.ts,
                );
              } else if (businessPlan.feedFilter === FeedFilter.Media) {
                await businessFeedItemsApi.getMediaFeedForBusinessPlan(
                  businessPlan.uid,
                  feedItems[feedItems.length - 1].data.ts,
                );
              } else {
                await businessFeedItemsApi.getFeedForBusinessPlan(
                  businessPlan.uid,
                  feedItems[feedItems.length - 1].data.ts,
                );
              }

              setIsLoading(false);
            }}
          >
            Load more feed
          </Button>
        )}
      </YStack>
    );
  },
);
